import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppService, CURRENT_CLIENT_ID_KEY, NavigationService, SessionStorageService, SortPipe } from '@core';
import { ClientStatus, type UserModel } from '@models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthService, UserManagementService } from '@services';
import { NotificationsService } from '@ui';
import { map, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { ClientManagementActions } from '../clients-managements';
import { SocketActions } from '../sockets';
import { AuthActions } from './auth.actions';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private notificationService = inject(NotificationsService);
  private authService = inject(AuthService);
  private appService = inject(AppService);
  private matDialog = inject(MatDialog);
  private navigationService = inject(NavigationService);
  private sessionStorageService = inject(SessionStorageService);
  private store = inject(Store);
  private userManagementService = inject(UserManagementService);
  private sortPipe = new SortPipe();

  addNewClientSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientManagementActions.addChildClientSuccess),
        tap(async client => {
          this.store.dispatch(
            AuthActions.superAdminClientCreated({
              newClient: {
                clientName: client.newClient.clientName,
                clientShortName: client.newClient.shortName,
                description: client.newClient.description || '',
                friendlyName: client.newClient.friendlyName,
                id: client.newClient.id,
                isManagingClient: client.newClient.isManagingClient,
                roleId: '',
                status: ClientStatus.ACTIVE,
                twoWayCommunication: client.newClient.twoWayCommunication,
              },
            }),
          );
          await this.router.navigate(['client-management', 'client-details', client.newClient.id]);
          this.notificationService.showSuccessMessage(
            `The client ${client.newClient.friendlyName} was successfully  created`,
          );
        }),
      ),
    { dispatch: false },
  );

  clientIdChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientManagementActions.clientIdChanged),
      tap(({ clientId }) => {
        if (clientId !== this.navigationService.getCurrentClientId()) {
          this.sessionStorageService.clear();
        }
      }),
      map(() => SocketActions.reconnet()),
    );
  });

  login$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.login),
    tap(action => localStorage.setItem('user', JSON.stringify(action.user))),
    switchMap(action => {
      const returnUrl = this.router.parseUrl(this.router.url).queryParams['returnUrl'];
      const returnUrlClientId = returnUrl ? this.router.parseUrl(returnUrl).queryParams[CURRENT_CLIENT_ID_KEY] : '';
      let clientId = this.appService.currentClient || returnUrlClientId;
      const clients = this.sortPipe.transform(action.user.userClients!, 'asc', 'friendlyName');
      const availableUserClient = clients.find(({ id }) => id === clientId);

      clientId = availableUserClient ? availableUserClient.id : clients[0].id;

      return [
        SocketActions.connect(),
        ClientManagementActions.clientIdChanged({ clientId }),
      ];
    }),
  ));

  logout$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.logout),
    tap(async () => {
      const returnUrl = `${window.location.pathname.replace('/', '')}${window.location.search}`;

      localStorage.removeItem('user');
      this.sessionStorageService.clear();
      this.appService.isInitailizedAppSubscription = false;
      await this.router.navigate([''], { queryParams: returnUrl ? { returnUrl } : {} });
    }),
    map(() => SocketActions.disconnect()),
  ));

  sendPasswordRecovery$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.passwordRecovery),
      switchMap(action =>
        this.authService.passwordRecovery(action.recoveryData).pipe(
          map(() => AuthActions.passwordRecovered()),
          catchError(error => of(AuthActions.authActionError({ error }))),
        ),
      ),
    );
  });

  passwordRecovered$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.passwordRecovered),
        tap(async () => {
          await this.router.navigateByUrl('password-recovery/success');
        }),
      );
    },
    { dispatch: false },
  );

  forgotPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      switchMap(action =>
        this.authService.forgotPassword(action.email).pipe(
          map(() => AuthActions.forgotPasswordSuccess()),
          catchError(error => of(AuthActions.authActionError({ error }))),
        ),
      ),
    );
  });

  forgotPasswordSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        tap(async () => {
          await this.router.navigateByUrl('forgot-password/success');
        }),
      );
    },
    { dispatch: false },
  );

  userErrorHandler$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.authActionError),
        tap(async () => {
          this.notificationService.showErrorMessage('Error: Please try again later');
          await this.router.navigateByUrl('');
        }),
      );
    },
    { dispatch: false },
  );

  userChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authChangeUserPassword),
      switchMap(action =>
        this.authService.changePassword(action.data).pipe(
          map(() => {
            this.notificationService.showSuccessMessage(
              'User password successfully changed. Please log in with new password',
            );

            return AuthActions.authUserPasswordChanged();
          }),
          catchError(error => of(AuthActions.authActionError({ error }))),
        ),
      ),
    ),
  );

  userPasswordChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authUserPasswordChanged),
      map(() => {
        this.matDialog.closeAll();

        return AuthActions.logout();
      }),
    ),
  );

  refreshProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshProfile),
      switchMap(() =>
        this.authService.getProfile().pipe(
          map(user => AuthActions.refreshProfileSuccess({ user })),
          catchError(error => of(AuthActions.authActionError({ error }))),
        ),
      ),
    ),
  );

  refreshProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.refreshProfileSuccess),
        tap(action => {
          const userProfile = localStorage.getItem('user');

          if (userProfile) {
            localStorage.setItem('user', JSON.stringify({ ...JSON.parse(userProfile), ...action.user }));
          }
        }),
      ),
    { dispatch: false },
  );

  updatePersonalUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updatePersonalData),
      switchMap(action =>
        this.userManagementService.updatePersonalUserData<UserModel>(action.data).pipe(
          map(user => AuthActions.updatePersonalDataSuccess({ user })),
          catchError(error => of(AuthActions.authActionError({ error }))),
        ),
      ),
    ),
  );

  updatePersonalNotificationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updateNotificationData),
      switchMap(action =>
        this.userManagementService
          .updateUserData<UserModel>(this.appService.currentClient, action.userId, action.data)
          .pipe(
            map(user => AuthActions.updatePersonalDataSuccess({ user })),
            catchError(error => of(AuthActions.authActionError({ error }))),
          ),
      ),
    ),
  );

  updatePersonalDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updatePersonalDataSuccess),
        tap(user => {
          this.notificationService.showSuccessMessage('Your profile was successfully updated');
          const userProfile = localStorage.getItem('user');

          if (userProfile) {
            const userProfileObject = JSON.parse(userProfile);

            localStorage.setItem('user', JSON.stringify({ ...userProfileObject, ...user.user }));
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  updateClientList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.superAdminClientCreated),
        tap(client => {
          const userProfile = localStorage.getItem('user');

          if (userProfile && client) {
            const userProfileObject: UserModel = JSON.parse(userProfile);

            userProfileObject.userClients?.push(client.newClient);
            localStorage.setItem('user', JSON.stringify(userProfileObject));
          }
        }),
      ),
    {
      dispatch: false,
    },
  );
}
